import { Card, CardContent } from '@/components/ui/card';
import { Home, Users, TrendingUp, Calendar, DollarSign, MapPin } from 'lucide-react';

interface TownStatisticsProps {
  townName: string;
  state: string | null;
  zipCodes: string[] | null;
  areasCount: number;
  listingsCount: number;
  medianHomePrice: number | null;
  marketTrendPercent: number | null;
  avgDaysOnMarket: number | null;
  population: number | null;
}

export function TownStatistics({ 
  townName, 
  state, 
  zipCodes, 
  areasCount, 
  listingsCount,
  medianHomePrice,
  marketTrendPercent,
  avgDaysOnMarket,
  population
}: TownStatisticsProps) {
  // Format median home price
  const formatPrice = (price: number | null) => {
    if (!price) return '—';
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(1)}M`;
    }
    return `$${Math.round(price / 1000)}K`;
  };

  // Format population
  const formatPopulation = (pop: number | null) => {
    if (!pop) return '—';
    if (pop >= 100000) {
      return `~${Math.round(pop / 1000)}K`;
    }
    return `~${pop.toLocaleString()}`;
  };

  const stats = [
    {
      icon: MapPin,
      label: 'Neighborhoods',
      value: areasCount > 0 ? areasCount.toString() : '—',
      description: 'Distinct areas to explore',
    },
    {
      icon: DollarSign,
      label: 'Median Home Price',
      value: formatPrice(medianHomePrice),
      description: 'Based on recent sales',
    },
    {
      icon: TrendingUp,
      label: 'Market Trend',
      value: marketTrendPercent ? `+${marketTrendPercent}%` : '—',
      description: 'Year-over-year growth',
    },
    {
      icon: Calendar,
      label: 'Avg. Days on Market',
      value: avgDaysOnMarket ? avgDaysOnMarket.toString() : '—',
      description: 'Time to sell',
    },
    {
      icon: Users,
      label: 'Population',
      value: formatPopulation(population),
      description: 'Estimated residents',
    },
  ];

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-display font-bold mb-2">
          {townName} at a Glance
        </h2>
        <p className="text-muted-foreground mb-8">
          Key statistics and market insights for {townName}, {state || 'MA'}
          {zipCodes && zipCodes.length > 0 && (
            <span className="ml-1">
              (ZIP: {zipCodes.join(', ')})
            </span>
          )}
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} className="text-center hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <stat.icon className="h-6 w-6 mx-auto mb-2 text-primary" />
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-sm font-medium text-foreground">{stat.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
